/**
 * WebSocket 消息模型定义
 *
 * 对应后端 models.Message 结构体
 * 这是一个嵌套的消息结构，顶层是信封，根据 event_type 填充不同的内容
 */

import { EVENT_TYPE, MSG_TYPE, ACK_STATUS, SYSTEM_TYPE } from './eventTypes';

// ============ 工厂函数：创建文本消息 ============

/**
 * 创建文本消息
 *
 * @param {Object} params
 * @param {string} params.toUser - 接收者用户ID
 * @param {string} params.fromUser - 发送者用户ID
 * @param {string} params.content - 消息内容
 * @returns {Object} 完整的消息对象
 *
 * @example
 * const msg = createTextMessage({
 *   toUser: '123',
 *   fromUser: '456',
 *   content: 'Hello!'
 * });
 */
export function createTextMessage({ toUser, fromUser, content }) {
  if (!toUser || !fromUser || !content) {
    throw new Error('toUser, fromUser, and content are required');
  }

  return {
    msg_id: crypto.randomUUID(),
    event_type: EVENT_TYPE.MESSAGE,
    from_user: fromUser,
    to_user: toUser,
    timestamp: Date.now(),
    message: {
      msg_type: MSG_TYPE.TEXT,
      text: {
        content: content,
      },
    },
  };
}

/**
 * 创建图片消息
 *
 * @param {Object} params
 * @param {string} params.toUser - 接收者用户ID
 * @param {string} params.fromUser - 发送者用户ID
 * @param {string} params.url - 图片URL
 * @param {number} [params.width] - 图片宽度
 * @param {number} [params.height] - 图片高度
 * @param {number} [params.size] - 文件大小（字节）
 * @param {string} [params.format] - 图片格式 (jpg/png/gif)
 */
export function createImageMessage({ toUser, fromUser, url, width, height, size, format }) {
  if (!toUser || !fromUser || !url) {
    throw new Error('toUser, fromUser, and url are required');
  }

  return {
    msg_id: crypto.randomUUID(),
    event_type: EVENT_TYPE.MESSAGE,
    from_user: fromUser,
    to_user: toUser,
    timestamp: Date.now(),
    message: {
      msg_type: MSG_TYPE.IMAGE,
      image: {
        url,
        ...(width && { width }),
        ...(height && { height }),
        ...(size && { size }),
        ...(format && { format }),
      },
    },
  };
}

/**
 * 创建文件消息
 *
 * @param {Object} params
 * @param {string} params.toUser - 接收者用户ID
 * @param {string} params.fromUser - 发送者用户ID
 * @param {string} params.url - 文件URL
 * @param {string} params.fileName - 文件名
 * @param {number} params.size - 文件大小（字节）
 * @param {string} [params.mimeType] - MIME类型
 */
export function createFileMessage({ toUser, fromUser, url, fileName, size, mimeType }) {
  if (!toUser || !fromUser || !url || !fileName || !size) {
    throw new Error('toUser, fromUser, url, fileName, and size are required');
  }

  return {
    msg_id: crypto.randomUUID(),
    event_type: EVENT_TYPE.MESSAGE,
    from_user: fromUser,
    to_user: toUser,
    timestamp: Date.now(),
    message: {
      msg_type: MSG_TYPE.FILE,
      file: {
        url,
        file_name: fileName,
        size,
        ...(mimeType && { mime_type: mimeType }),
      },
    },
  };
}

// ============ 工厂函数：创建 ACK 消息 ============

/**
 * 创建消息确认 (ACK)
 *
 * @param {Object} params
 * @param {string} params.origMsgId - 原始消息的 msg_id
 * @param {string} params.status - ACK状态: received/delivered/read/failed
 * @param {string} [params.error] - 错误信息（status=failed时使用）
 * @returns {Object} ACK消息对象
 *
 * @example
 * const ack = createAckMessage({
 *   origMsgId: 'msg-uuid-123',
 *   status: ACK_STATUS.RECEIVED
 * });
 */
export function createAckMessage({ origMsgId, status, error }) {
  if (!origMsgId || !status) {
    throw new Error('origMsgId and status are required');
  }

  if (!Object.values(ACK_STATUS).includes(status)) {
    throw new Error(`Invalid ACK status: ${status}`);
  }

  return {
    msg_id: crypto.randomUUID(),
    event_type: EVENT_TYPE.ACK,
    timestamp: Date.now(),
    ack: {
      orig_msg_id: origMsgId,
      status: status,
      ...(error && { error: error }),
    },
  };
}

// ============ 工厂函数：创建系统消息 ============

/**
 * 创建系统消息
 *
 * @param {Object} params
 * @param {string} params.type - 系统消息类型 (使用 SYSTEM_TYPE 常量)
 * @param {string} params.message - 人类可读的消息
 * @param {Object} [params.data] - 额外数据
 * @returns {Object} 系统消息对象
 *
 * @example
 * const sysMsg = createSystemMessage({
 *   type: SYSTEM_TYPE.USER_ONLINE,
 *   message: '用户上线了',
 *   data: { user_id: '123' }
 * });
 */
export function createSystemMessage({ type, message, data }) {
  if (!type || !message) {
    throw new Error('type and message are required');
  }

  if (!Object.values(SYSTEM_TYPE).includes(type)) {
    throw new Error(`Invalid system message type: ${type}`);
  }

  return {
    msg_id: crypto.randomUUID(),
    event_type: EVENT_TYPE.SYSTEM,
    timestamp: Date.now(),
    system: {
      type: type,
      message: message,
      ...(data && { data }),
    },
  };
}

// ============ 验证函数 ============

/**
 * 验证消息格式是否正确
 *
 * @param {Object} msg - 待验证的消息对象
 * @returns {boolean} 是否为有效消息
 */
export function isValidMessage(msg) {
  if (!msg || typeof msg !== 'object') {
    return false;
  }

  // 检查顶层必需字段
  if (!msg.msg_id || !msg.event_type || !msg.timestamp) {
    return false;
  }

  // 根据不同事件类型验证
  switch (msg.event_type) {
    case EVENT_TYPE.MESSAGE:
      return validateMessageContent(msg);

    case EVENT_TYPE.ACK:
      return validateAckContent(msg);

    case EVENT_TYPE.SYSTEM:
      return validateSystemContent(msg);

    default:
      // 未知类型，保持宽容
      return true;
  }
}

function validateMessageContent(msg) {
  if (!msg.message || !msg.from_user || !msg.to_user) {
    return false;
  }

  const { msg_type } = msg.message;
  if (!msg_type || !Object.values(MSG_TYPE).includes(msg_type)) {
    return false;
  }

  // 验证对应的消息体存在
  switch (msg_type) {
    case MSG_TYPE.TEXT:
      return !!(msg.message.text && msg.message.text.content);
    case MSG_TYPE.IMAGE:
      return !!(msg.message.image && msg.message.image.url);
    case MSG_TYPE.VIDEO:
      return !!(msg.message.video && msg.message.video.url);
    case MSG_TYPE.AUDIO:
      return !!(msg.message.audio && msg.message.audio.url);
    case MSG_TYPE.FILE:
      return !!(
        msg.message.file &&
        msg.message.file.url &&
        msg.message.file.file_name &&
        msg.message.file.size
      );
    case MSG_TYPE.CARD:
      return !!(msg.message.card && msg.message.card.title);
    default:
      return false;
  }
}

function validateAckContent(msg) {
  if (!msg.ack) {
    return false;
  }

  const { orig_msg_id, status } = msg.ack;
  return !!(orig_msg_id && status && Object.values(ACK_STATUS).includes(status));
}

function validateSystemContent(msg) {
  if (!msg.system) {
    return false;
  }

  const { type, message } = msg.system;
  return !!(type && message && Object.values(SYSTEM_TYPE).includes(type));
}

// ============ 辅助函数 ============

/**
 * 从消息中提取文本内容（用于显示）
 *
 * @param {Object} msg - 消息对象
 * @returns {string} 显示文本
 */
export function getMessageDisplayText(msg) {
  if (!msg || msg.event_type !== EVENT_TYPE.MESSAGE) {
    return '';
  }

  const { message } = msg;
  if (!message) return '';

  switch (message.msg_type) {
    case MSG_TYPE.TEXT:
      return message.text?.content || '';
    case MSG_TYPE.IMAGE:
      return '[图片]';
    case MSG_TYPE.VIDEO:
      return '[视频]';
    case MSG_TYPE.AUDIO:
      return '[语音]';
    case MSG_TYPE.FILE:
      return `[文件] ${message.file?.file_name || ''}`;
    case MSG_TYPE.CARD:
      return `[卡片] ${message.card?.title || ''}`;
    default:
      return '[未知消息类型]';
  }
}

/**
 * 判断是否为自己发送的消息
 *
 * @param {Object} msg - 消息对象
 * @param {string} myUserId - 当前用户ID
 * @returns {boolean}
 */
export function isMyMessage(msg, myUserId) {
  return msg && msg.from_user === String(myUserId);
}
